import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AppRoute } from '@/common/app/routes';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { SecondaryButton } from '@/components/ui/Button/Button';
import { clearMaintenanceMode } from '@/redux/reducers/userReducer';
import { type RootState } from '@/redux/store';

import css from './style.module.scss';

const pad = (n: number): string => String(n).padStart(2, '0');

const MaintenancePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const wakeUpAt = useSelector(
    (state: RootState) => state.user.maintenanceWakeUpAt,
  );

  const secondsRemaining = wakeUpAt
    ? Math.max(
        0,
        Math.floor((new Date(wakeUpAt).getTime() - Date.now()) / 1000),
      )
    : 0;

  const seconds = useCountDownTimer(secondsRemaining);

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  useEffect(() => {
    if (secondsRemaining > 0 && seconds === 0) {
      dispatch(clearMaintenanceMode());
      navigate(AppRoute.MAIN);
    }
  }, [seconds, secondsRemaining, dispatch, navigate]);

  const handleClose = (): void => {
    if (Telegram?.WebApp?.initData) {
      Telegram.WebApp.close();
    }
  };

  return (
    <div className={css.page}>
      <div className={css.heroWrapper}>
        <img
          className={css.heroImage}
          src="/img/icons/maintenance/maintenance.png"
          alt="maintenance"
        />
        <div className={css.heroGradient} />
      </div>

      <div className={css.content}>
        <h1 className={css.title}>{t('Technical break')}</h1>
        <p className={css.subtitle}>
          {t('The game is temporarily paused for all players')}
        </p>

        <div className={css.spinner}>
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className={css.spinnerDot} />
          ))}
        </div>

        {wakeUpAt && (
          <div className={css.countdown}>
            <div className={css.timeGroup}>
              <div className={css.digits}>
                <span className={css.digit}>{pad(hours)[0]}</span>
                <span className={css.digit}>{pad(hours)[1]}</span>
              </div>
              <span className={css.timeLabel}>{t('hrs')}</span>
            </div>
            <span className={css.colon}>:</span>
            <div className={css.timeGroup}>
              <div className={css.digits}>
                <span className={css.digit}>{pad(minutes)[0]}</span>
                <span className={css.digit}>{pad(minutes)[1]}</span>
              </div>
              <span className={css.timeLabel}>{t('min')}</span>
            </div>
          </div>
        )}

        <p className={css.patience}>{t('Thanks for your patience!')}</p>
      </div>

      <div className={css.closeWrapper}>
        <SecondaryButton text={t('Close')} onClick={handleClose} />
      </div>

      <div className={css.glowBottom} />
    </div>
  );
};

export default MaintenancePage;
